// =====================================================================
// src/lib/v2/bizCode.js
//
// Short, share-friendly business IDs in the format `XXX-XXXX`.
// Matches V1's group-code style (`MYZ-WPCD`) so business URLs stay
// consistent with how users already share batches.
//
// Alphabet: A-Z + 2-9 (no 0/O/1/I/L — ambiguous in handwriting/print).
// Total characters: 32. Length: 3+4 = 7 chars.
// Entropy: 32^7 ≈ 34 billion combos. Collisions are practically nil at
// V2.0 scale, but we still check Firestore before commit out of caution.
// =====================================================================

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

function randomChar() {
  return ALPHABET.charAt(Math.floor(Math.random() * ALPHABET.length));
}

/** Generates a single candidate code like 'MYZ-WPCD'. */
function generateCode() {
  let prefix = '';
  for (let i = 0; i < 3; i++) prefix += randomChar();
  let suffix = '';
  for (let i = 0; i < 4; i++) suffix += randomChar();
  return `${prefix}-${suffix}`;
}

/**
 * Generates a unique business code and confirms it doesn't already
 * exist at /businesses/{code}. Retries up to `maxAttempts` times before
 * throwing. In practice the very first attempt almost always succeeds.
 */
export async function generateUniqueBizCode(maxAttempts = 8) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const code = generateCode();
    const snap = await getDoc(doc(db, 'businesses', code));
    if (!snap.exists()) return code;
    // Collision — try again. Logs help if we ever see this in practice.
    console.warn(`[bizCode] collision on ${code} (attempt ${attempt + 1})`);
  }
  throw new Error('Could not generate a unique business code after retries');
}

/** Quick sanity check — used by the registration form's pre-submit validation. */
export function isValidBizCode(code) {
  return typeof code === 'string' && /^[A-Z2-9]{3}-[A-Z2-9]{4}$/.test(code);
}
